import { useQuery } from '@apollo/client';
import { WarningAmber } from '@mui/icons-material';
import {
  Autocomplete,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  MenuItem,
  TextField,
  Tooltip
} from '@mui/material';

import clsx from 'clsx';
import { Field, useFormikContext } from 'formik';
import { debounce } from 'lodash';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import QUERY from 'gql/queries/ModulesService/getGroupings.gql';
import USER_QUERY from 'gql/queries/currentUser.gql';
import USERS_QUERY from 'gql/queries/getProfs.gql';

import StyledDropzone from 'app/components/Dropzone';
import GQLGuard from 'app/components/GQLGuard';
import {
  Lecturer,
  SubjectType,
  UsersQueryArgs
} from 'app/containers/AWPM-FWPM/ModuleSelections/models';
import { userDataToLecturerOption } from 'app/containers/AWPM-FWPM/helper';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { UserType } from 'utils/ACLHelper';
import { sortCourses } from 'utils/courses';

interface Props {
  subjectType: SubjectType;
}

function sortLecturerData(a: { label: string }, b: { label: string }) {
  if (a.label < b.label) {
    return -1;
  }
  if (a.label > b.label) {
    return 1;
  }

  return 0;
}

export default function GeneralInfo({ subjectType }: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const groupingsQuery = useQuery<any, {}>(QUERY, {
    context: { clientName: 'federation' },
    variables: { all: true }
  });

  const usersQuery = useQuery<any, UsersQueryArgs>(USERS_QUERY, {
    variables: {
      type: UserType.SHIBBOLETH,
      entitlements: [
        'IDM_Professoren_Gesamt',
        'IDM_Lehrbeauftragte_Gesamt',
        'IDM_Mitarbeiter_Gesamt'
      ],
      limit: 0
    },
    context: { clientName: 'federation' }
  });

  const userQuery = useQuery<any, any>(USER_QUERY, {
    context: { clientName: 'federation' }
  });

  const { setFieldValue, values, errors, touched, setFieldTouched, handleChange, handleBlur } =
    useFormikContext<any>();

  const lecturerDataToValue = lecturerData => [
    ...(!!values?.lecturers?.length
      ? values.lecturers.map(lecturer =>
          userDataToLecturerOption(
            lecturerData?.users.find(data => {
              return parseInt(data.id, 10) === lecturer;
            }),
            t
          )
        )
      : []),
    ...(!!values?.unregisteredLecturers?.length ? values.unregisteredLecturers : [])
  ];

  const changeHandler = (field, text) => {
    setFieldValue(field, text);
  };

  const debouncedChangeHandler = useCallback(
    debounce((field, text) => {
      changeHandler(field, text);
    }, 500),
    []
  );

  return (
    <GQLGuard
      query={[groupingsQuery, userQuery, usersQuery]}
      onError={error => {
        if (error?.graphQLErrors[0]?.extensions?.status === 404) {
          dispatch(AlertActions.error(`${t(translations.pages.modules.alerts.failedLoading())}`));
        } else {
          dispatch(
            AlertActions.error(
              `${t(translations.pages.modules.alerts.failedLoading())}: ${error.message}`
            )
          );
        }
        navigate('/', { replace: true });
        return null;
      }}
    >
      {([groupingsData, userData, lecturerData]) => {
        const sortedCourses = sortCourses(userData.currentUser?.courses.slice());
        return (
          <Grid container>
            {values.subjectType !== SubjectType.Awpm && (
              <FormControl
                disabled={!!values.id && !values.isDefault}
                className="moduleModal__body__input"
                required
                fullWidth
              >
                <FormLabel className="moduleModal__body__input__label">
                  {t(translations.pages.modules.faculty())}
                </FormLabel>
                <TextField
                  disabled={!!values.id && !values.isDefault}
                  select
                  defaultValue=""
                  id="responsibleCourseOfStudy"
                  name="responsibleCourseOfStudy"
                  value={values.responsibleCourseOfStudy || ''}
                  error={!!(touched.responsibleCourseOfStudy && errors.responsibleCourseOfStudy)}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                >
                  {sortedCourses.map(course => {
                    return (
                      <MenuItem value={course.id} key={course.id}>
                        {'(' + course.shorthand + ') ' + course.name}
                      </MenuItem>
                    );
                  })}
                </TextField>
                {touched.responsibleCourseOfStudy && errors.responsibleCourseOfStudy && (
                  <FormHelperText
                    error={!!(touched.responsibleCourseOfStudy && errors.responsibleCourseOfStudy)}
                  >
                    {<>{errors.responsibleCourseOfStudy}</>}
                  </FormHelperText>
                )}
              </FormControl>
            )}
            <FormControl className="moduleModal__body__input" required fullWidth>
              <FormLabel className="moduleModal__body__input__label">
                {t(translations.pages.modules.create.GeneralInfo.name())}
              </FormLabel>
              <Field
                as={TextField}
                id="name"
                name="name"
                fullWidth
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name && <>{errors.name}</>}
                required
              />
            </FormControl>
            <FormControl className="moduleModal__body__input" fullWidth>
              <FormLabel className="moduleModal__body__input__label">
                <Grid container>
                  <Grid item xs={6}>
                    {t(translations.pages.modules.create.GeneralInfo.moduleOfficer())}
                  </Grid>
                  <Grid item container xs={6} justifyContent="flex-end">
                    <Tooltip title={t(translations.pages.modules.moduleResponsibleTooltip())}>
                      <WarningAmber fontSize="medium" />
                    </Tooltip>
                  </Grid>
                </Grid>
              </FormLabel>
              <Autocomplete
                id="moduleOfficer"
                getOptionDisabled={option => values?.moduleOfficer === option.id}
                value={
                  !!values.moduleOfficer
                    ? userDataToLecturerOption(
                        lecturerData?.users.find(
                          data => parseInt(data.id, 10) === values.moduleOfficer
                        ),
                        t
                      )
                    : null
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(event, newValue) => {
                  setFieldValue('moduleOfficer', newValue?.id ?? null);
                }}
                options={lecturerData?.users
                  .map(item => userDataToLecturerOption(item, t))
                  .sort(sortLecturerData)}
                renderInput={params => <TextField {...params} />}
              />
            </FormControl>
            <FormControl className="moduleModal__body__input" fullWidth>
              <FormLabel className="moduleModal__body__input__label">
                <Grid container>
                  <Grid item xs={6}>
                    {t(translations.pages.modules.create.GeneralInfo.lecturer.title())}
                  </Grid>
                </Grid>
              </FormLabel>
              <Autocomplete
                id="lecturers"
                multiple
                getOptionDisabled={option =>
                  !!values?.lecturers?.find(lecturer => option.id === lecturer)
                }
                value={lecturerDataToValue(lecturerData)}
                isOptionEqualToValue={(option, value) => {
                  if (typeof value === 'string') {
                    return false;
                  }
                  return option.id === value;
                }}
                onChange={(event, newValue) => {
                  let userIds: number[] = [];
                  let lecturers: Lecturer[] = [];
                  let unregisteredUser: string[] = [];
                  newValue.forEach(lecturer => {
                    if (typeof lecturer === 'string') {
                      unregisteredUser.push(lecturer);
                    } else {
                      userIds.push(lecturer.id);
                      lecturers.push(lecturer.label);
                    }
                  });
                  setFieldValue('lecturers', userIds);
                  setFieldValue('lecturersUsers', lecturers);
                  setFieldValue('unregisteredLecturers', unregisteredUser);
                }}
                options={lecturerData?.users
                  .map(item => userDataToLecturerOption(item, t))
                  .sort(sortLecturerData)}
                freeSolo
                autoSelect={true}
                renderInput={params => <TextField {...params} />}
              />
            </FormControl>
            {subjectType === SubjectType.Awpm && (
              <FormControl className="moduleModal__body__input" fullWidth required>
                <FormLabel className="moduleModal__body__input__label" required>
                  {t(translations.pages.modules.create.GeneralInfo.grouping.title())}
                </FormLabel>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      id="groupingId"
                      name="groupingId"
                      value={values?.groupingId}
                      onChange={e => setFieldValue('groupingId', e.target.value)}
                      onBlur={handleBlur}
                      fullWidth
                      required
                      error={!!(touched.groupingId && errors.groupingId)}
                    >
                      {groupingsData.getGroupings.map(grouping => {
                        const groupingIdToInt = parseInt(grouping.id, 10);
                        return (
                          <MenuItem key={groupingIdToInt} value={groupingIdToInt}>
                            {grouping.name}
                          </MenuItem>
                        );
                      })}
                    </TextField>
                    {touched.groupingId && errors.groupingId && (
                      <FormHelperText error={!!(touched.groupingId && errors.groupingId)}>
                        {<>{errors.groupingId}</>}
                      </FormHelperText>
                    )}
                  </Grid>
                </Grid>
              </FormControl>
            )}
            <FormControl className="moduleModal__body__input" fullWidth>
              <FormLabel className="moduleModal__body__input__label">
                {t(translations.pages.modules.create.GeneralInfo.description())}
              </FormLabel>
              <Field name="description">
                {({ field }) => (
                  <div data-text-editor="description">
                    <ReactQuill
                      className={clsx('moduleModal__body__input__rte', {
                        'moduleModal__body__input__rte--error':
                          touched.description && errors.description
                      })}
                      value={field.value}
                      onChange={(_content, _delta, _source, editor) =>
                        debouncedChangeHandler('description', editor.getHTML())
                      }
                      onBlur={(_prev, _source, editor) => {
                        setFieldTouched('description', true);
                      }}
                      theme="snow"
                      bounds={`[data-text-editor="description"]`}
                      modules={{
                        toolbar: [
                          ['bold', 'italic'],
                          ['link'],
                          [
                            { list: 'ordered' },
                            { list: 'bullet' },
                            { indent: '-1' },
                            { indent: '+1' }
                          ],
                          ['clean']
                        ],
                        clipboard: { matchVisual: false }
                      }}
                      formats={['bold', 'italic', 'link', 'list', 'bullet', 'indent']}
                    />
                    {touched.description && errors.description && (
                      <FormHelperText error>{<>{errors.description}</>}</FormHelperText>
                    )}
                  </div>
                )}
              </Field>
            </FormControl>
            <FormControl className="moduleModal__body__input" fullWidth>
              <FormLabel className="moduleModal__body__input__label">
                {t(translations.pages.modules.create.GeneralInfo.attachments())}
              </FormLabel>
            </FormControl>
            <StyledDropzone
              name="attachments"
              setFieldValue={setFieldValue}
              value={values.attachments}
              errors={errors}
              placeholder={t(translations.pages.modules.create.GeneralInfo.attachmentPlaceholder())}
              className="moduleModal__body__input__dropzone"
              multiple
            />
          </Grid>
        );
      }}
    </GQLGuard>
  );
}
